import React from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import LandingTabs from "../components/landingtabs";
import SEO from "../components/SEO";
import { personalInfo, seoConfig } from "../config";

const Landing = () => {
  return (
    <main className="min-h-screen">
      <SEO 
        title={seoConfig.pages.home.title}
        description={seoConfig.pages.home.description}
        keywords={seoConfig.pages.home.keywords.join(", ")}
        url="/"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": seoConfig.siteName,
            "url": seoConfig.siteUrl
          },
          {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": personalInfo.name,
            "url": seoConfig.siteUrl,
            "jobTitle": personalInfo.title,
            "sameAs": [
              personalInfo.social.twitter,
              personalInfo.social.linkedin,
              personalInfo.social.github,
              personalInfo.social.medium
            ]
          }
        ]}
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 relative overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-wrap items-center">
              <div className="w-full text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="relative inline-block mb-6"
                >
                  <img
                    src={process.env.PUBLIC_URL + personalInfo.profileImage}
                    height={200}
                    width={200}
                    alt={personalInfo.name}
                    className="relative shadow-2xl rounded-full h-auto align-middle border-4 border-white dark:border-gray-800 hover:scale-105 transition-transform duration-300"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <h1 className="text-5xl md:text-6xl font-bold mb-4">
                    <span className="text-gray-900 dark:text-white">{personalInfo.name}</span>
                  </h1>
                  
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="flex flex-wrap justify-center gap-3 mb-6"
                  >
                    {personalInfo.tags.map((tag, index) => (
                      <motion.span
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: 0.5 + (index * 0.1) }}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        #{tag.replace(/\s+/g, '')}
                      </motion.span>
                    ))}
                  </motion.div>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8"
                  >
                    {personalInfo.tagline}
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1 }}
                    className="flex gap-4 justify-center"
                  >
                    <motion.a
                      href="#contact"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="btn-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        // Simple scroll to contact tab (you may need to adjust this)
                        window.scrollTo({ top: document.querySelector('.landing-tabs')?.offsetTop - 100, behavior: 'smooth' });
                      }}
                    >
                      Get In Touch
                    </motion.a>
                    <motion.a
                      href={personalInfo.resumePath}
                      target="_blank"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="btn-secondary"
                    >
                      View Resume
                    </motion.a>
                  </motion.div>
                </motion.div>
              </div>

              {/* Tabs Section */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="lg:pt-12 pt-12 w-full px-4 text-center mt-12 landing-tabs"
              >
                <LandingTabs />
              </motion.div>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    </main>
  );
};

export default Landing;
